// ---------------------------------------------------------------------------
// WANM centralized site configuration.
// Update these values as official details become available — no rebuild of
// components required. Anything set to null / "Coming soon" is treated as
// "not yet available" throughout the UI, and analytics with a null ID never
// initialize (see cookies/TrackingLoader).
// ---------------------------------------------------------------------------

export const siteConfig = {
  company: {
    name: "WANM — We Are Nation Media",
    shortName: "WANM",
    positioning: "Performance Marketing & Affiliate Network",
    // Legal entity name — placeholder until officially provided
    legalEntity: null, // e.g. "We Are Nation Media, Inc."
  },

  // Contact — WANM has no official domain/email/phone/address yet.
  // Keep as "Coming soon" placeholders; replace when available.
  contact: {
    email: null, // e.g. "hello@wanm.com" — when null the UI shows "Coming soon"
    phone: null,
    address: null,
  },

  // Only confirmed social profile. Do not add others until official.
  social: {
    linkedin: "https://www.linkedin.com/company/wanm/",
  },

  // Lead notification recipient. Left null on purpose — backend uses its own
  // OWNER_EMAIL env (placeholder) until the official business inbox exists.
  leadNotificationEmail: null,

  // Legal / compliance
  legal: {
    effectiveDate: "Coming soon",
    contactEmail: null, // falls back to "Coming soon" in policy pages
  },

  // ------------------------------------------------------------------------
  // Analytics & marketing tracking. IDs are null until real ones are provided.
  // Nothing initializes while an ID is null AND/OR consent is not granted.
  // ------------------------------------------------------------------------
  tracking: {
    googleAnalyticsId: null, // e.g. "G-XXXXXXXXXX"
    metaPixelId: null,       // e.g. "1234567890"
    // Additional affiliate / conversion trackers can be added here later.
  },
};

// Helper: value or "Coming soon"
export const orComingSoon = (v) => (v && String(v).trim() ? v : "Coming soon");

// ------------------------------------------------------------------------
// Cookie category definitions (drives banner + preference center + policy).
// ------------------------------------------------------------------------
export const COOKIE_CATEGORIES = [
  {
    id: "necessary",
    required: true,
    en: {
      name: "Strictly Necessary",
      desc: "Required for core website functionality, security, authentication, form handling and remembering your consent choice. These cannot be disabled.",
    },
    es: {
      name: "Estrictamente Necesarias",
      desc: "Necesarias para el funcionamiento básico del sitio, la seguridad, la autenticación, los formularios y para recordar tu elección de consentimiento. No se pueden desactivar.",
    },
  },
  {
    id: "analytics",
    required: false,
    en: {
      name: "Analytics",
      desc: "Help us understand how visitors use the site so we can measure and improve performance. Reserved for tools like Google Analytics — not active until configured and consented.",
    },
    es: {
      name: "Analítica",
      desc: "Nos ayudan a entender cómo se usa el sitio para medir y mejorar el rendimiento. Reservado para herramientas como Google Analytics; no se activa hasta configurarlo y consentirlo.",
    },
  },
  {
    id: "marketing",
    required: false,
    en: {
      name: "Marketing / Advertising",
      desc: "Reserved for future advertising, conversion and affiliate tracking tools (e.g. Meta Pixel). Not active until real configurations are provided and consented.",
    },
    es: {
      name: "Marketing / Publicidad",
      desc: "Reservado para futuras herramientas de publicidad, conversión y seguimiento de afiliación (p. ej. Meta Pixel). No se activa hasta proporcionar configuraciones reales y consentirlas.",
    },
  },
];

// ------------------------------------------------------------------------
// Cookie inventory — ONLY technologies actually in use today.
// Future integrations are documented in the policy as "future", not here.
// ------------------------------------------------------------------------
export const COOKIE_INVENTORY = [
  {
    name: "wanm_consent",
    provider: "WANM (first-party)",
    purpose: "Stores your cookie consent preferences.",
    category: "necessary",
    duration: "6 months",
    party: "First-party",
    active: true,
  },
  {
    name: "wanm_lang",
    provider: "WANM (first-party)",
    purpose: "Remembers your language selection (English / Spanish).",
    category: "necessary",
    duration: "Persistent (localStorage)",
    party: "First-party",
    active: true,
  },
  {
    name: "wanm_token",
    provider: "WANM (first-party)",
    purpose: "Authentication for the admin area (only set when an admin signs in).",
    category: "necessary",
    duration: "Session",
    party: "First-party",
    active: true,
  },
];

export default siteConfig;
